import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store";
import type { RootState } from "../../store";
import {
  fetchAgentLookups,
  createAgentLookup,
  updateAgentLookup,
  deleteAgentLookup,
  resetLookupSaveError,
  resetLookupUpdateError,
  resetLookupDeleteError,
} from "../../store/agentManagementSlice";
import { useMsal } from "@azure/msal-react";
import { clearUser } from "../../store/authSlice";
import Sidebar from "../../components/Sidebar";
import {
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Typography,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Snackbar,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import "../Dashboard.scss";
import styles from "../AgentManagement/agentmanagement.module.scss";
import { useNavigate } from "react-router-dom";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

export default function AgentLookupManagementPage(): JSX.Element {
  const user = useSelector((state: RootState) => state.auth.user);
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    items,
    loading,
    saving,
    saveError,
    updatingCategoryId,
    updateError,
    deletingCategoryId,
    deleteError,
  } = useSelector((state: RootState) => state.agentLookup);

  const [categoryName, setCategoryName] = useState("");
  const [statusMessage, setStatusMessage] = useState<{
    text: string;
    severity: "success" | "info";
  } | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [categoryToEdit, setCategoryToEdit] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [editCategoryName, setEditCategoryName] = useState("");

  const [searchInputValue, setSearchInputValue] = useState("");
  const debouncedSearchTerm = useDebounce(searchInputValue, 300);
  const filteredItems = useMemo(() => {
    const term = debouncedSearchTerm.toLowerCase().trim();
    if (!term) return items;
    return items.filter((item) => item.category.toLowerCase().includes(term));
  }, [items, debouncedSearchTerm]);
  const handleClearSearch = () => setSearchInputValue("");

  useEffect(() => {
    dispatch(fetchAgentLookups());
  }, [dispatch]);

  const onClickLogout = () => {
    dispatch(clearUser());
    instance.logoutRedirect({ postLogoutRedirectUri: "/" });
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const category = categoryName.trim();
    if (!category) return;

    const existing = items.find(
      (item) => item.category.toLowerCase() === category.toLowerCase()
    );
    if (existing) {
      setCategoryName("");
      setStatusMessage({
        text: `"${existing.category}" already exists — using the existing category.`,
        severity: "info",
      });
      return;
    }

    try {
      await dispatch(createAgentLookup({ category })).unwrap();
      setCategoryName("");
      setStatusMessage({ text: `"${category}" added successfully.`, severity: "success" });
    } catch {
      // error surfaced via saveError alert
    }
  };

  const handleEditClick = (categoryId: number, category: string) => {
    setCategoryToEdit({ id: categoryId, name: category });
    setEditCategoryName(category);
  };

  const handleCancelEdit = () => {
    setCategoryToEdit(null);
    setEditCategoryName("");
  };

  const handleConfirmEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryToEdit) return;
    const newName = editCategoryName.trim();
    if (!newName || newName === categoryToEdit.name) {
      handleCancelEdit();
      return;
    }

    try {
      await dispatch(
        updateAgentLookup({ categoryId: categoryToEdit.id, category: newName })
      ).unwrap();
      handleCancelEdit();
      setStatusMessage({ text: `"${newName}" saved successfully.`, severity: "success" });
    } catch {
      // error surfaced via updateError alert; keep the dialog open to retry
    }
  };

  const handleDeleteClick = () => {
    if (!categoryToEdit) return;
    setCategoryToDelete(categoryToEdit);
  };

  const handleCancelDelete = () => {
    setCategoryToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;
    const { name } = categoryToDelete;
    try {
      await dispatch(deleteAgentLookup(categoryToDelete.id)).unwrap();
      setCategoryToDelete(null);
      handleCancelEdit();
      setStatusMessage({ text: `"${name}" deleted successfully.`, severity: "success" });
    } catch {
      setCategoryToDelete(null);
      // error surfaced via deleteError alert; edit dialog stays open
    }
  };

  return (
    <div className="dashboard-root">
      <Sidebar
        activeNav="agent-lookup-management"
        user={user || undefined}
        onLogout={onClickLogout}
      />
      <main className="main-area">
        <header className="header">
          <div className="header-left">
            <IconButton
              onClick={() => navigate("/agent-management")}
              sx={{ mr: 2 }}
              aria-label="Back to agent management"
            >
              <ArrowBackIcon />
            </IconButton>
            <h1 className="page-title">Categories</h1>
          </div>
        </header>

        <div className="scrollContent">
          <div className={styles.content}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add new values here to populate the Category dropdown in Agent
              Management.
            </Typography>

            {saveError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(resetLookupSaveError())}>
                {saveError}
              </Alert>
            )}
            <Card className={styles.agentsCard} elevation={2}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Add New
                </Typography>
                <Box
                  component="form"
                  onSubmit={handleAdd}
                  sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}
                >
                  <TextField
                    label="Category name"
                    size="small"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                    disabled={saving || !categoryName.trim()}
                  >
                    Add
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card className={styles.agentsCard} elevation={2} sx={{ mt: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Typography variant="h6">
                    Existing Categories ({filteredItems.length})
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Search categories"
                    value={searchInputValue}
                    onChange={(e) => setSearchInputValue(e.target.value)}
                    sx={{ minWidth: 240 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: searchInputValue && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={handleClearSearch} edge="end">
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ maxHeight: 600, overflowY: "auto" }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Category</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredItems.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                {debouncedSearchTerm
                                  ? "No categories found for your search"
                                  : "No categories yet. Add one above."}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredItems.map((item) => (
                            <TableRow key={item.category_id}>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <LocalOfferOutlinedIcon fontSize="small" color="action" />
                                  <Typography variant="body2" fontWeight={600}>
                                    {item.category}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Edit category">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleEditClick(item.category_id, item.category)
                                      }
                                      disabled={
                                        updatingCategoryId === item.category_id ||
                                        deletingCategoryId === item.category_id
                                      }
                                    >
                                      {updatingCategoryId === item.category_id ||
                                      deletingCategoryId === item.category_id ? (
                                        <CircularProgress size={18} />
                                      ) : (
                                        <EditIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Dialog
        open={!!categoryToEdit}
        onClose={handleCancelEdit}
        aria-labelledby="edit-category-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <form onSubmit={handleConfirmEdit}>
          <DialogTitle id="edit-category-dialog-title">Edit Category</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              label="Category name"
              fullWidth
              size="small"
              value={editCategoryName}
              onChange={(e) => setEditCategoryName(e.target.value)}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDeleteClick}
              color="error"
              startIcon={<DeleteIcon fontSize="small" />}
              disabled={updatingCategoryId === categoryToEdit?.id}
              sx={{ mr: "auto" }}
            >
              Delete
            </Button>
            <Button onClick={handleCancelEdit}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                updatingCategoryId === categoryToEdit?.id || !editCategoryName.trim()
              }
              startIcon={
                updatingCategoryId === categoryToEdit?.id ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={!!categoryToDelete}
        onClose={handleCancelDelete}
        aria-labelledby="delete-category-dialog-title"
      >
        <DialogTitle id="delete-category-dialog-title">
          Delete Category
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{categoryToDelete?.name}</strong>? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deletingCategoryId === categoryToDelete?.id}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!statusMessage}
        autoHideDuration={statusMessage?.severity === "info" ? 5000 : 3000}
        onClose={() => setStatusMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={statusMessage?.severity || "success"}
          variant="filled"
          onClose={() => setStatusMessage(null)}
        >
          {statusMessage?.text}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!updateError}
        autoHideDuration={6000}
        onClose={() => dispatch(resetLookupUpdateError())}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => dispatch(resetLookupUpdateError())}
        >
          {updateError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!deleteError}
        autoHideDuration={6000}
        onClose={() => dispatch(resetLookupDeleteError())}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => dispatch(resetLookupDeleteError())}
        >
          {deleteError}
        </Alert>
      </Snackbar>
    </div>
  );
}
